import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Cart = () => {
    const { cartItems, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();
    const navigate = useNavigate();

    if (cartItems.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header />
                <div className="container mx-auto px-4 py-16">
                    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-12 text-center">
                        <ShoppingCart className="w-24 h-24 text-gray-300 mx-auto mb-6" />
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
                        <p className="text-gray-600 mb-6">Add some products to get started!</p>
                        <button
                            onClick={() => navigate('/shop')}
                            className="bg-primary hover:bg-primary-strong text-white px-8 py-3 rounded-lg font-medium transition-colors"
                        >
                            Start Shopping
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />

            <div className="container mx-auto px-4 py-8">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Shopping Cart</h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* Cart Items */}
                    <div className="lg:col-span-2 space-y-4">
                        {cartItems.map((item) => (
                            <div key={item.id} className="bg-white rounded-lg shadow-md p-4 md:p-6">
                                <div className="flex gap-4">
                                    {/* Image */}
                                    <img
                                        src={item.image}
                                        alt={item.name}
                                        className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg flex-shrink-0"
                                    />

                                    {/* Details */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-start mb-2">
                                            <div>
                                                <h3 className="font-semibold text-lg text-gray-900 mb-1">{item.name}</h3>
                                                <p className="text-sm text-gray-500">{item.category}</p>
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                                                title="Remove item"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>

                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-4">
                                            {/* Quantity */}
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm text-gray-600">Quantity:</span>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                        className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                                                    >
                                                        <Minus className="w-4 h-4 mx-auto" />
                                                    </button>
                                                    <span className="w-12 text-center font-medium">{item.quantity}</span>
                                                    <button
                                                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                        className="w-8 h-8 border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                                                    >
                                                        <Plus className="w-4 h-4 mx-auto" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Price */}
                                            <div className="text-right">
                                                <p className="text-sm text-gray-500">₹{item.price.toLocaleString('en-IN')} each</p>
                                                <p className="text-xl font-bold text-primary">
                                                    ₹{(item.price * item.quantity).toLocaleString('en-IN')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Order Summary */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
                            <h2 className="text-xl font-bold mb-4">Order Summary</h2>

                            <div className="space-y-3 mb-6">
                                <div className="flex justify-between text-gray-600">
                                    <span>Items ({getCartCount()})</span>
                                    <span>₹{getCartTotal().toLocaleString('en-IN')}</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                    <span>Shipping</span>
                                    <span className="text-green-600">FREE</span>
                                </div>
                                <div className="border-t pt-3">
                                    <div className="flex justify-between text-lg font-bold">
                                        <span>Total</span>
                                        <span className="text-primary">₹{getCartTotal().toLocaleString('en-IN')}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => navigate('/checkout')}
                                className="w-full bg-primary hover:bg-primary-strong text-white py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 mb-3"
                            >
                                Proceed to Checkout
                                <ArrowRight className="w-5 h-5" />
                            </button>

                            <button
                                onClick={() => navigate('/shop')}
                                className="w-full border border-gray-300 hover:bg-gray-50 py-2 rounded-lg transition-colors"
                            >
                                Continue Shopping
                            </button>

                            {/* Trust Badges */}
                            <div className="mt-6 pt-6 border-t space-y-2 text-sm text-gray-600">
                                <p className="flex items-center gap-2">
                                    <span className="text-green-600">✓</span>
                                    Secure checkout
                                </p>
                                <p className="flex items-center gap-2">
                                    <span className="text-green-600">✓</span>
                                    Free shipping on all orders
                                </p>
                                <p className="flex items-center gap-2">
                                    <span className="text-green-600">✓</span>
                                    Expert support available
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Cart;
